import {OrderStatus, SORT_KEYS_ORDER_VALIDATE} from '../../app/modules/orders/core/_const'
import {WEIGHT_UNIT} from '../constants'

interface Parcel {
  id: number
  package_id: number | null
  package_type: string | null
  length: number
  width: number
  height: number
  weight: number
  dimension_unit: string
  weight_unit?: string
  package_count?: number
}

/**
 * Calculate the total number of parcels in the list.
 */
export function getParcelCount(parcels?: Parcel[] | undefined | null): number {
  if (!parcels || parcels.length === 0) return 0
  return parcels.reduce((total, parcel) => total + (parcel.package_count || 1), 0)
}

/**
 * Calculate the total weight (weight × package_count) of the parcels list, rounded to two decimal places.
 */
export function getTotalWeight(parcels?: Parcel[] | null): number {
  if (!parcels || parcels.length === 0) return 0
  const total = parcels.reduce((total, parcel) => {
    const count = parcel.package_count ?? 1
    const weight = parcel.weight ?? 0
    return total + weight * count
  }, 0)
  return Math.round(total * 100) / 100
}

/**
 * Return the weight unit of the first parcel, or default to WEIGHT_UNIT.OZ if not available.
 */
export function getWeightUnit(parcels: Parcel[] | undefined | null): string {
  if (!parcels || parcels.length === 0) return WEIGHT_UNIT.OZ
  return parcels[0].weight_unit ?? WEIGHT_UNIT.OZ
}

/**
 * Recursively extracts all error paths from a nested error object
 * 
 * @param errors - Formik error
 * @param prefix - Internal use only: prefix for nested paths (used in recursion)
 * 
 * @returns Array of dot-notation paths to all error locations in the object

 * @remarks
 * - Handles nested objects and arrays
 * - Special cases for 'to' and 'from' keys (adds them directly without recursion)
 * - Returns paths in dot notation (e.g., 'parent.child.0.field')
 */
export const getErrorPaths = (errors: Record<string, any>, prefix = ''): string[] => {
  return Object.keys(errors).reduce<string[]>((paths, key) => {
    const currentPath = prefix ? `${prefix}.${key}` : key
    const value = errors[key]

    if (key === 'to' || key === 'from') {
      paths.push(currentPath)
      return paths
    }

    if (typeof value === 'object' && value !== null) {
      if (Array.isArray(value)) {
        value.forEach((item, index) => {
          if (item !== null && typeof item === 'object') {
            paths.push(...getErrorPaths(item, `${currentPath}.${index}`))
          }
        })
      } else {
        paths.push(...getErrorPaths(value, currentPath))
      }
    } else {
      paths.push(currentPath)
    }

    return paths
  }, [])
}

/**
 * Gets the sort order index for a given path based on predefined key ordering.
 *
 * @param path - A string path where the first segment is used as the lookup key.
 *              Expected format: "key.rest.of.path" or just "key"
 *
 * @returns The index position of the first path segment in the SORT_KEYS_ORDER_VALIDATE array.
 *          Returns -1 if the key is not found in the array.
 *
 * @example
 * // Assuming SORT_KEYS_ORDER_VALIDATE = ['name', 'age', 'date']
 * getKeyOrder('name.first'); // returns 0
 * getKeyOrder('age');       // returns 1
 * getKeyOrder('unknown');   // returns -1
 */
export const getKeyOrder = (path: string) => {
  const key = path.split('.')[0]
  return SORT_KEYS_ORDER_VALIDATE.indexOf(key)
}

export const convertOrderCopy = (order: any) => {
  return {
    ...order,
    parcels: order?.parcels?.map(({id, ...rest}) => rest),
    id: null,
    service: null,
    carrier: null,
    shipments:
      order?.status === OrderStatus.SHIPPED
        ? order.shipments.map((s, i, arr) =>
            i === arr.length - 1 ? {...s, service: null, carrier: null} : s
          )
        : order.shipments,
  }
}
